import React, { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import { apiClient } from '../services/apiClient';
import {
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';

const tableStyles = {
  tableCell: {
    padding: '8px 16px',
    height: '50px',
    whiteSpace: 'nowrap',
  },
  headerCell: {
    padding: '8px 16px',
    height: '50px',
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
  }
};

export default function Settings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [settingForm, setSettingForm] = useState({
    key: '',
    value: '',
    description: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/admin/settings');
      setSettings(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setSettingForm({
        key: item.key,
        value: item.value,
        description: item.description || ''
      });
    } else {
      setSelectedItem(null);
      setSettingForm({
        key: '',
        value: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setSettingForm({
      key: '',
      value: '',
      description: ''
    });
  };

  const handleOpenDeleteDialog = (item) => {
    setSelectedItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      // For stt-engine, wrap the value in quotes since it's stored as JSON
      const valueToSend = settingForm.key === 'stt-engine' 
        ? `"${settingForm.value}"`
        : settingForm.value;
        
      if (selectedItem) {
        await apiClient.put(`/api/admin/settings/${selectedItem.key}`, {
          value: valueToSend,
          description: settingForm.description
        });
      } else {
        await apiClient.post('/api/admin/settings', {
          ...settingForm,
          value: valueToSend
        });
      }
      await fetchData();
      handleCloseDialog();
      showSnackbar(selectedItem ? 'Paramètre mis à jour avec succès' : 'Paramètre créé avec succès');
    } catch (error) {
      console.error('Error saving setting:', error);
      if (error.response?.status === 409) {
        showSnackbar('Cette clé existe déjà', 'error');
      } else {
        showSnackbar('Erreur lors de l\'enregistrement', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await apiClient.delete(`/api/admin/settings/${selectedItem.key}`);
      await fetchData();
      handleCloseDeleteDialog();
      showSnackbar('Paramètre supprimé avec succès');
    } catch (error) {
      console.error('Error deleting setting:', error);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatValue = (value) => {
    try {
      // Try to parse as JSON and format nicely
      const parsed = JSON.parse(value);
      if (typeof parsed === 'string') {
        return parsed;
      }
      return JSON.stringify(parsed, null, 2);
    } catch {
      // If not JSON, return as is
      return value;
    }
  };

  // Function to render the value input based on the key
  const renderValueInput = () => {
    if (settingForm.key === 'stt-engine') {
      // For stt-engine, show a dropdown
      return (
        <FormControl fullWidth>
          <InputLabel id="stt-engine-label">Moteur STT</InputLabel>
          <Select
            labelId="stt-engine-label"
            id="stt-engine"
            value={settingForm.value.replace(/^"|"$/g, '')} // Remove quotes if they exist
            onChange={(e) => setSettingForm({ ...settingForm, value: e.target.value })}
            required
            label="Moteur STT"
          >
            <MenuItem value="azure">Azure</MenuItem>
            <MenuItem value="webspeech">Web Speech</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // For all other settings, show the textarea
    return (
      <textarea
        id="value"
        name="value"
        rows={3}
        value={settingForm.value}
        onChange={(e) => setSettingForm({ ...settingForm, value: e.target.value })}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        required
        placeholder='Ex: "azure" ou {"engine": "azure", "config": {...}}'
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      {/* Settings Section */}
      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Gestion des paramètres
          </h2>
          <button
            onClick={() => handleOpenDialog()}
            className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
          >
            <AddIcon className="-ml-1 mr-2 h-5 w-5" />
            Nouveau paramètre
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valeur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settings.map((setting) => (
                <tr key={setting.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {setting.key}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate" title={formatValue(setting.value)}>
                      {formatValue(setting.value)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs truncate" title={setting.description}>
                      {setting.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenDialog(setting)}
                      className="text-blue-600 hover:text-blue-900 mr-2"
                    >
                      <EditIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteDialog(setting)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <DeleteIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {settings.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun paramètre trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedItem ? 'Modifier le paramètre' : 'Créer un paramètre'}
              </h3>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="key" className="block text-sm font-medium text-gray-700 mb-1">
                      Clé
                    </label>
                    <input
                      type="text"
                      id="key"
                      name="key"
                      value={settingForm.key}
                      onChange={(e) => setSettingForm({ ...settingForm, key: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                      disabled={!!selectedItem}
                    />
                  </div>

                  <div>
                    <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-1">
                      Valeur
                    </label>
                    {renderValueInput()}
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={2}
                      value={settingForm.description}
                      onChange={(e) => setSettingForm({ ...settingForm, description: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Description optionnelle du paramètre"
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                    ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                    focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                >
                  {saving ? (
                    <>
                      <CircularProgress size={20} className="mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    selectedItem ? 'Enregistrer' : 'Créer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer le paramètre "{selectedItem?.key}" ?
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseDeleteDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} className="mr-2" />
                    Suppression...
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
} 